import React, { useId } from 'react';

/**
 * Conistra brand mark — the "loop & spark".
 *
 * The open loop is the autopilot cycle that never stops turning; the four-point
 * spark seated in its opening is the Oracle. Together they read as a "C".
 *
 * Geometry is fixed (ported 1:1 from brand/conistra-mark.svg) on a 200×200
 * viewBox. Only the paint changes with `variant`:
 *   - gradient (default): the one signal gradient  #ff2f8e → #ff5a5f → #f9820c
 *   - white:              solid #fff  (for use on the gradient / photos)
 *   - ink:                solid #1b1a17 (single-color on light surfaces)
 */

const RING_PATH = 'M 157.6 107.1 A 58 58 0 1 1 92.9 42.4';
const SPARK_PATH =
  'M 141.0 17.0 Q 150.0 50.0 183.0 59.0 Q 150.0 68.0 141.0 101.0 ' +
  'Q 132.0 68.0 99.0 59.0 Q 132.0 50.0 141.0 17.0 Z';

export type ConistraMarkVariant = 'gradient' | 'white' | 'ink';

type ConistraMarkProps = {
  size?: number;
  variant?: ConistraMarkVariant;
  className?: string;
  title?: string;
};

function ConistraMark({
  size = 96,
  variant = 'gradient',
  className,
  title = 'Conistra',
}: ConistraMarkProps) {
  // Unique gradient id per instance so multiple marks never collide.
  const gradId = `conistra-grad-${useId().replace(/:/g, '')}`;
  const paint =
    variant === 'white' ? '#ffffff' : variant === 'ink' ? '#1b1a17' : `url(#${gradId})`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {variant === 'gradient' && (
        <defs>
          <linearGradient id={gradId} x1="30" y1="20" x2="180" y2="185" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ff2f8e" />
            <stop offset="0.5" stopColor="#ff5a5f" />
            <stop offset="1" stopColor="#f9820c" />
          </linearGradient>
        </defs>
      )}
      <path d={RING_PATH} stroke={paint} strokeWidth={26} strokeLinecap="round" />
      <path d={SPARK_PATH} fill={paint} />
    </svg>
  );
}

export type LogoProps = {
  /** Height of the mark in px (wordmark scales with it). */
  size?: number;
  variant?: ConistraMarkVariant;
  /** Wordmark color. Defaults to brand ink; use "#fff" on dark/gradient. */
  wordmarkColor?: string;
  className?: string;
};

/** Horizontal lockup: mark + lowercase "conistra" wordmark. */
export function Logo({ size = 32, variant = 'gradient', wordmarkColor, className }: LogoProps) {
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(size * 0.34) }}
    >
      <ConistraMark size={size} variant={variant} />
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: Math.round(size * 0.72),
          letterSpacing: '-0.03em',
          color: wordmarkColor ?? 'var(--color-ink)',
          lineHeight: 1,
        }}
      >
        conistra
      </span>
    </span>
  );
}

export default ConistraMark;
